import React, { useState } from 'react'
import { 
  HelpCircle, 
  X, 
  BookOpen, 
  Video, 
  MessageCircle, 
  ExternalLink,
  ChevronRight,
  Play,
  Edit3,
  Gamepad2,
  Settings,
  Download,
  Share2
} from 'lucide-react'

export const HelpModal = ({ isOpen, onClose }) => {
  const [activeSection, setActiveSection] = useState('getting-started')

  const sections = [
    {
      id: 'getting-started',
      title: '快速开始',
      icon: <Play size={20} />,
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 p-6 rounded-lg">
            <h3 className="text-xl font-semibold text-white mb-2">欢迎使用 AltLife 互动故事平台！</h3>
            <p className="text-dark-300">在这里，您可以创作、编辑和体验精彩的互动故事。让我们开始您的创作之旅吧！</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-dark-700 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <h4 className="text-white font-medium">选择故事类型</h4>
              </div>
              <p className="text-dark-300 text-sm">从科幻、奇幻、悬疑等多种类型中选择您喜欢的题材。</p>
            </div>

            <div className="bg-dark-700 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <h4 className="text-white font-medium">设置故事背景</h4>
              </div>
              <p className="text-dark-300 text-sm">描述故事的世界观、主要角色和基本情节设定。</p>
            </div>

            <div className="bg-dark-700 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <h4 className="text-white font-medium">AI 生成故事</h4>
              </div>
              <p className="text-dark-300 text-sm">AI 将根据您的设定生成精彩的故事内容和分支选择。</p>
            </div>

            <div className="bg-dark-700 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-sm">4</span>
                </div>
                <h4 className="text-white font-medium">编辑和优化</h4>
              </div>
              <p className="text-dark-300 text-sm">在编辑器中修改故事内容，添加图片，完善细节。</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'features',
      title: '功能介绍',
      icon: <BookOpen size={20} />,
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-dark-700 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <Edit3 size={24} className="text-blue-400 mr-3" />
                <h3 className="text-lg font-semibold text-white">故事创作</h3>
              </div>
              <ul className="space-y-2 text-dark-300">
                <li>• AI 智能故事生成</li>
                <li>• 多种故事类型选择</li>
                <li>• 自定义角色和设定</li>
                <li>• 分支剧情设计</li>
              </ul>
            </div>

            <div className="bg-dark-700 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <Gamepad2 size={24} className="text-green-400 mr-3" />
                <h3 className="text-lg font-semibold text-white">故事播放</h3>
              </div>
              <ul className="space-y-2 text-dark-300">
                <li>• 沉浸式阅读体验</li>
                <li>• 互动选择系统</li>
                <li>• 进度保存功能</li>
                <li>• 多结局支持</li>
              </ul>
            </div>

            <div className="bg-dark-700 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <Settings size={24} className="text-purple-400 mr-3" />
                <h3 className="text-lg font-semibold text-white">个性化设置</h3>
              </div>
              <ul className="space-y-2 text-dark-300">
                <li>• 主题和语言切换</li>
                <li>• 自动保存设置</li>
                <li>• 通知偏好管理</li>
                <li>• 音效和动画控制</li>
              </ul>
            </div>

            <div className="bg-dark-700 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <Download size={24} className="text-yellow-400 mr-3" />
                <h3 className="text-lg font-semibold text-white">数据管理</h3>
              </div>
              <ul className="space-y-2 text-dark-300">
                <li>• 本地数据存储</li>
                <li>• 数据导入导出</li>
                <li>• 云端同步支持</li>
                <li>• 备份恢复功能</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'tutorials',
      title: '使用教程',
      icon: <Video size={20} />,
      content: (
        <div className="space-y-6">
          <div className="bg-dark-700 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">创作流程详解</h3>
            
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="text-white font-medium mb-2">第一步：开始创作</h4>
                <p className="text-dark-300 text-sm mb-2">点击"开始创作"按钮，进入故事创作界面。</p>
                <div className="bg-dark-800 p-3 rounded text-sm text-dark-300">
                  💡 提示：选择您熟悉的故事类型，这样更容易创作出精彩内容。
                </div>
              </div>

              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="text-white font-medium mb-2">第二步：设置故事背景</h4>
                <p className="text-dark-300 text-sm mb-2">详细描述故事的世界观、主要角色和基本情节。</p>
                <div className="bg-dark-800 p-3 rounded text-sm text-dark-300">
                  💡 提示：越详细的设定，AI 生成的故事越符合您的期望。
                </div>
              </div>

              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="text-white font-medium mb-2">第三步：生成和编辑</h4>
                <p className="text-dark-300 text-sm mb-2">AI 生成故事后，您可以在编辑器中进一步修改和完善。</p>
                <div className="bg-dark-800 p-3 rounded text-sm text-dark-300">
                  💡 提示：可以添加图片、修改对话、调整情节发展。
                </div>
              </div>

              <div className="border-l-4 border-yellow-500 pl-4">
                <h4 className="text-white font-medium mb-2">第四步：播放体验</h4>
                <p className="text-dark-300 text-sm mb-2">在播放器中体验您的创作，测试互动选择的效果。</p>
                <div className="bg-dark-800 p-3 rounded text-sm text-dark-300">
                  💡 提示：可以邀请朋友一起体验您的故事作品。
                </div>
              </div>
            </div>
          </div>

          <div className="bg-dark-700 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">高级技巧</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-dark-800 p-4 rounded-lg">
                <h4 className="text-white font-medium mb-2">🎨 图片使用技巧</h4>
                <p className="text-dark-300 text-sm">为关键镜头添加图片，提升故事的可视化效果。建议使用高清图片，尺寸控制在 800x600 左右。</p>
              </div>
              
              <div className="bg-dark-800 p-4 rounded-lg">
                <h4 className="text-white font-medium mb-2">📝 对话写作技巧</h4>
                <p className="text-dark-300 text-sm">为不同角色设计独特的说话风格，让对话更生动有趣。注意保持角色性格的一致性。</p>
              </div>
              
              <div className="bg-dark-800 p-4 rounded-lg">
                <h4 className="text-white font-medium mb-2">🌳 分支设计技巧</h4>
                <p className="text-dark-300 text-sm">设计有意义的选择分支，让每个选择都能影响故事走向。避免无意义的选择。</p>
              </div>
              
              <div className="bg-dark-800 p-4 rounded-lg">
                <h4 className="text-white font-medium mb-2">💾 保存和分享</h4>
                <p className="text-dark-300 text-sm">定期保存您的工作，可以导出故事文件进行备份。也可以分享给朋友一起创作。</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'faq',
      title: '常见问题',
      icon: <MessageCircle size={20} />,
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <details className="group bg-dark-700 rounded-lg">
              <summary className="flex justify-between items-center p-4 cursor-pointer text-white hover:text-blue-400 transition-colors">
                <span className="font-medium">如何保存我的故事？</span>
                <span className="group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="px-4 pb-4 text-dark-300">
                您的故事会自动保存到本地存储。您也可以点击"我的故事"按钮查看所有保存的故事，或使用导出功能备份到文件。
              </div>
            </details>

            <details className="group bg-dark-700 rounded-lg">
              <summary className="flex justify-between items-center p-4 cursor-pointer text-white hover:text-blue-400 transition-colors">
                <span className="font-medium">如何分享我的故事？</span>
                <span className="group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="px-4 pb-4 text-dark-300">
                您可以将故事导出为文本文件，然后通过社交媒体、邮件或其他方式分享给朋友。我们也在开发在线分享功能。
              </div>
            </details>

            <details className="group bg-dark-700 rounded-lg">
              <summary className="flex justify-between items-center p-4 cursor-pointer text-white hover:text-blue-400 transition-colors">
                <span className="font-medium">故事生成失败怎么办？</span>
                <span className="group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="px-4 pb-4 text-dark-300">
                请检查网络连接，或尝试重新生成。如果问题持续存在，可以尝试简化故事设定，或联系技术支持。
              </div>
            </details>

            <details className="group bg-dark-700 rounded-lg">
              <summary className="flex justify-between items-center p-4 cursor-pointer text-white hover:text-blue-400 transition-colors">
                <span className="font-medium">如何添加图片到故事中？</span>
                <span className="group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="px-4 pb-4 text-dark-300">
                在故事编辑器中，点击图片按钮可以上传本地图片，或使用 AI 生成图片。建议使用高清图片以获得最佳效果。
              </div>
            </details>

            <details className="group bg-dark-700 rounded-lg">
              <summary className="flex justify-between items-center p-4 cursor-pointer text-white hover:text-blue-400 transition-colors">
                <span className="font-medium">如何设计分支选择？</span>
                <span className="group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="px-4 pb-4 text-dark-300">
                在编辑器中，您可以为每个选择点添加不同的选项。每个选项都会影响后续的故事发展，建议设计有意义的选择。
              </div>
            </details>

            <details className="group bg-dark-700 rounded-lg">
              <summary className="flex justify-between items-center p-4 cursor-pointer text-white hover:text-blue-400 transition-colors">
                <span className="font-medium">数据会丢失吗？</span>
                <span className="group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="px-4 pb-4 text-dark-300">
                不会！您的数据会安全保存在本地存储中。我们建议定期导出数据进行备份。
              </div>
            </details>
          </div>
        </div>
      )
    }
  ]

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      <div className="bg-dark-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col transform transition-transform duration-300 scale-95">
        <div className="flex justify-between items-center p-5 border-b border-dark-700">
          <h2 className="text-2xl font-bold text-white">帮助中心</h2>
          <button onClick={onClose} className="text-dark-300 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-grow overflow-hidden">
          {/* 侧边导航 */}
          <nav className="w-64 bg-dark-900 p-4 space-y-2 border-r border-dark-700">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-colors ${
                  activeSection === section.id 
                    ? 'bg-dark-700 text-white' 
                    : 'text-dark-300 hover:bg-dark-700 hover:text-white'
                }`}
              >
                {section.icon}
                <span className="ml-3">{section.title}</span>
              </button>
            ))}
          </nav>

          {/* 内容区域 */}
          <div className="flex-grow p-6 overflow-y-auto">
            {sections.find(s => s.id === activeSection)?.content}
          </div>
        </div>

        {/* 底部操作 */}
        <div className="flex justify-between items-center p-5 border-t border-dark-700">
          <div className="text-dark-400 text-sm">
            需要更多帮助？请联系技术支持
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => window.open('mailto:support@altlife.com', '_blank')}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <MessageCircle size={16} className="mr-2" />
              联系支持
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-dark-600 text-white rounded-lg hover:bg-dark-500 transition-colors"
            >
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HelpModal
